/**
 * Grade livre do Início (24 colunas × linhas de 20 px + 12 px de espaço): mover, redimensionar e acomodar os
 * outros quadros sem sobrepor (empurra para baixo e depois sobe tudo o que puder).
 */
export const COLS = 24;
export const ROW = 20;
export const GAP = 12;
export interface Box { id: string; x: number; y: number; w: number; h: number }

const hit = (a: Box, b: Box) => a.id !== b.id && a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
const clampBox = <T extends Box>(b: T): T => {
  const w = Math.max(1, Math.min(COLS, b.w));
  return { ...b, w, h: Math.max(1, b.h), x: Math.max(0, Math.min(COLS - w, b.x)), y: Math.max(0, b.y) };
};

/** Põe o quadro `moved` onde foi pedido e acomoda os demais. */
export function resolve<T extends Box>(items: T[], movedId: string | null): T[] {
  const moved = items.find((i) => i.id === movedId);
  const placed: T[] = moved ? [clampBox(moved)] : [];
  const rest = items.filter((i) => i.id !== movedId).map(clampBox).sort((a, b) => a.y - b.y || a.x - b.x);
  for (const it of rest) {
    let cur = { ...it };
    for (let guard = 0; guard < 500; guard++) {
      const c = placed.find((p) => hit(cur, p));
      if (!c) break;
      cur = { ...cur, y: c.y + c.h };
    }
    placed.push(cur);
  }
  return compact(placed, movedId);
}

/** Sobe cada quadro até encostar no de cima (na ordem de cima para baixo). */
export function compact<T extends Box>(items: T[], firstOnTie: string | null = null): T[] {
  const order = [...items].sort((a, b) => a.y - b.y || (a.id === firstOnTie ? -1 : b.id === firstOnTie ? 1 : a.x - b.x));
  const out: T[] = [];
  for (const it of order) {
    let cur = { ...it, y: 0 };
    for (let guard = 0; guard < 500; guard++) {
      const c = out.find((p) => hit(cur, p));
      if (!c) break;
      cur = { ...cur, y: c.y + c.h };
    }
    out.push(cur);
  }
  return out;
}

/** Setas: um passo para o lado (2 colunas) ou troca de lugar com o vizinho de cima/baixo. */
export function nudge<T extends Box>(items: T[], id: string, dir: "up" | "down" | "left" | "right"): T[] {
  const me = items.find((i) => i.id === id);
  if (!me) return items;
  const overlapCols = (o: Box) => o.x < me.x + me.w && me.x < o.x + o.w;
  let target = { ...me };
  if (dir === "left") target.x = Math.max(0, me.x - 2);
  if (dir === "right") target.x = Math.min(COLS - me.w, me.x + 2);
  if (dir === "up") {
    const above = items.filter((o) => o.id !== id && overlapCols(o) && o.y + o.h <= me.y).sort((a, b) => b.y - a.y)[0];
    if (!above) return items;
    target = { ...me, y: above.y };
  }
  if (dir === "down") {
    const below = items.filter((o) => o.id !== id && overlapCols(o) && o.y >= me.y + me.h).sort((a, b) => a.y - b.y)[0];
    if (!below) return items;
    target = { ...me, y: below.y + below.h };
  }
  return resolve(items.map((i) => (i.id === id ? target : i)), id);
}

/** Altura em pixels de um quadro com h linhas. */
export const px = (h: number) => h * ROW + (h - 1) * GAP;
/** Primeira linha livre (para adicionar um quadro no fim). */
export const bottom = (items: Box[]) => items.reduce((m, i) => Math.max(m, i.y + i.h), 0);
